// Shared HTTP foundation for the API layer: `createHttpClient` → a configured axios
// instance (retry + Sentry capture). Failures funnel through `captureApiError` so the
// dashboard tags are uniform. Per-service modules (medUsers, medBilling, …) build on
// it — see docs/backend-apis.md.
//
// DEV-35018 removed the second transport (`authedJson`, an Auth0 DPoP-authenticated
// fetch): with Auth0 gone from the funnel, every backend the web talks to is either
// unauthenticated or session-scoped, so plain axios covers all of it. The fetch-side
// error context builder lives on in modules/sentry/network (googleAddress uses it).
import axios, { type AxiosError, type AxiosInstance } from 'axios'
import retry, { exponentialDelay, isSafeRequestError } from 'axios-retry'

import { buildAxiosApiErrorContext, captureApiError } from '@/modules/sentry/network'

/** Optional user-flow context attached to a request; surfaces on the Sentry event. */
export interface SentryRequestContext {
	flow?: string
	step?: string
	/**
	 * Override the level `resolveLevel` derives from the status. Point of it: a transport
	 * failure defaults to `error`, which is right for a call the funnel depends on and wrong
	 * for one whose failure is a designed, handled outcome — e.g. the KYC precheck, where any
	 * failure simply means «show the ID-photo screen» and must not page anyone.
	 */
	level?: 'error' | 'warning'
}

// Lets callers pass `sentryContext` on an axios request config; the response-error
// interceptor reads it back off `error.config` (see buildAxiosApiErrorContext).
declare module 'axios' {
	interface AxiosRequestConfig {
		sentryContext?: SentryRequestContext
	}
}

interface HttpClientConfig {
	/** Sentry `api` tag — the owning GitHub repo (see docs/backend-apis.md). */
	api: string
	baseURL: string
	retry?: boolean
	/** When false, network errors are NOT auto-captured (the caller captures instead). */
	captureErrors?: boolean
	headers?: Record<string, string>
}

/** Create a configured axios instance with retry + Sentry error capture baked in. */
export function createHttpClient({
	api,
	baseURL,
	retry: useRetry = false,
	captureErrors = true,
	headers,
}: HttpClientConfig): AxiosInstance {
	const instance = axios.create({ baseURL, ...(headers && { headers }) })

	if (useRetry) {
		retry(instance, {
			shouldResetTimeout: true,
			retryDelay: exponentialDelay,
			retryCondition: (error) =>
				['ECONNABORTED', 'ERR_NETWORK'].includes(error.code ?? '') || isSafeRequestError(error),
		})
	}

	if (captureErrors) {
		instance.interceptors.response.use(
			(response) => response,
			(error: AxiosError) => {
				captureApiError(error, buildAxiosApiErrorContext(error, api))
				return Promise.reject(error)
			},
		)
	}

	return instance
}

/**
 * Has this error already been captured by the transport? True only for axios failures
 * (the interceptor above funnels every one of them through `captureApiError`).
 *
 * Callers that swallow errors need the distinction: an axios error is already an issue in
 * GlitchTip with its api/endpoint/status tags, while an Error thrown by our own code after a
 * 2xx (`empty response body`, a failed invariant, a TypeError) is reported nowhere. Lives here
 * so stores don't import axios just to ask.
 */
export function isTransportReportedError(error: unknown): boolean {
	return axios.isAxiosError(error)
}

/** HTTP status of a rejected axios request; undefined for network failures and non-axios errors. */
export function getResponseStatus(error: unknown): number | undefined {
	return axios.isAxiosError(error) ? error.response?.status : undefined
}
